/**
 * Brand mark icons for the supported warehouse types. The SVGs come from
 * `@rudderlabs/icons` (the RudderStack integration icon library, shared with the
 * webapp) and render through `PlatedBrandMark` — the official brand colors are baked into the asset, an
 * intentional exception to the no-hardcoded-color rule, and in dark the mark
 * sits on the round logo plate so dark ink (Postgres) stays visible.
 *
 * Each wrapper accepts `className` (merged last, so callers can override the
 * default size); the icons sit next to a text label, so they are `aria-hidden`.
 */
import { type IconName, iconUrl } from '@rudderlabs/icons';
import { Database } from 'lucide-react';
import * as React from 'react';

import { PlatedBrandMark } from '@/_shared/components/PlatedBrandMark';
import { cn } from '@/_shared/lib/utils';

interface IconProps {
  className?: string;
}

/** Render a library icon as a decorative, sized mark on the logo plate. */
function BrandIcon({ name, className }: { name: IconName; className?: string }): React.JSX.Element {
  return <PlatedBrandMark src={iconUrl(name)} className={cn('size-6', className)} />;
}

export function SnowflakeIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="snowflake" className={className} />;
}

export function RedshiftIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="redshift" className={className} />;
}

export function DatabricksIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="databricks" className={className} />;
}

export function BigQueryIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="bigquery" className={className} />;
}

export function PostgresIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="postgres" className={className} />;
}

export function ClickHouseIcon({ className }: IconProps): React.JSX.Element {
  return <BrandIcon name="clickhouse" className={className} />;
}

/** Fallback icon for unknown/unsupported backend warehouse types. */
export function GenericWarehouseIcon({ className }: IconProps): React.JSX.Element {
  return <Database className={className} />;
}
